<template>
  <div>
    <div class="namespace-main-settings-page" v-if="!isLoading">
      <h2 class="namespace-main-settings-page__title">Основная информация</h2>
      <div class="namespace-main-settings-page__content">
        <div class="namespace-main-settings-page__row">
          <TirPmInput
            v-model="formData.Name"
            class="namespace-main-settings-page__namespace-name"
            label="Название раздела"
            :is-with-hint="false"
          />
          <TirPmIconPicker v-model="selectedIcon" :icons="icons" :colors="colors" />
        </div>
      </div>

      <div class="namespace-main-settings-page__button">
        <TirPmButton
          :variant="TirPmButtonVariantEnum.PrimaryState"
          :size="TirPmButtonSizeEnum.Small"
          :is-loading="isLoadingSave"
          style="width: 6.625rem"
          :is-disabled="!isChangedForm"
          @click="saveNamespace"
        >
          Сохранить
        </TirPmButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  TirPmInput,
  TirPmIconPicker,
  IconType,
  IconColorType,
  SelectedIconModel,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from 'tir-components';
import { NamespaceFormDataModel, UpdateNamespaceModel } from './models';
import { AdapterService } from './adapter';
import { NamespaceModel } from '@/components/sidebar/models';

const emit = defineEmits<{
  /**Событие обновления неймспейса */
  (e: 'updateNamespacePage', namespace: NamespaceModel): void;
}>();

const props = defineProps({
  /**ID неймспейса */
  namespaceId: {
    type: String,
    required: true,
  },
});

const adapter = new AdapterService();

const lastFormDataStr = ref('');
/**Данные формы */
const formData = ref(new NamespaceFormDataModel());

const isChangedForm = computed(() => lastFormDataStr.value !== JSON.stringify(formData.value));

/**Выбранная иконка */
const selectedIcon = computed({
  get() {
    if (formData.value.IconId)
      return new SelectedIconModel({
        IconId: formData.value.IconId,
        ColorId: formData.value.ColorId,
      });
  },
  set(value) {
    formData.value = new NamespaceFormDataModel({
      ...formData.value,
      IconId: value.IconId,
      ColorId: value.ColorId,
    });
  },
});

/**Список иконок */
const icons = ref(new Array<IconType>());
/**Список цветов */
const colors = ref(new Array<IconColorType>());
/**Состояние загрузки */
const isLoading = ref(false);
/**Состояние загрузки при сохранении */
const isLoadingSave = ref(false);

/**Получить иконки */
const getIcons = async () => {
  const resp = await adapter.getIcons();
  if (resp.IsSuccess) icons.value = resp.Value;
};

/**Получить цвета иконок */
const getColors = async () => {
  const resp = await adapter.getColors();
  if (resp.IsSuccess) colors.value = resp.Value;
};

/**Получить неймспейс */
const getNamespace = async () => {
  const resp = await adapter.getNamespace(props.namespaceId);
  if (resp.IsSuccess)
    formData.value = new NamespaceFormDataModel({
      Name: resp.Value.Name,
      IconId: resp.Value.IconId,
      ColorId: resp.Value.ColorId,
    });
  lastFormDataStr.value = JSON.stringify(formData.value);
};

/**Сохранить данные неймспейса */
const saveNamespace = async () => {
  isLoadingSave.value = true;

  const resp = await adapter.updateNamespace(
    new UpdateNamespaceModel({
      Id: props.namespaceId,
      Name: formData.value.Name,
      IconId: formData.value.IconId,
      ColorId: formData.value.ColorId,
    }),
  );
  if (resp.IsSuccess) {
    await getNamespace();
  }
  isLoadingSave.value = false;

  const icon = icons.value.find((x) => x.IconId == selectedIcon.value.IconId)?.IconUrl;
  const color = colors.value.find((c) => c.ColorId == selectedIcon.value.ColorId)?.Color;

  const namespace = new NamespaceModel({
    Id: props.namespaceId,
    Name: formData.value.Name,
    IconId: formData.value.IconId,
    ColorId: formData.value.ColorId,
    IconUrl: icon,
    Color: color,
  });
  emit('updateNamespacePage', namespace);
};

/**Инициализация данных */
const initData = async () => {
  isLoading.value = true;
  await Promise.all([getIcons(), getColors(), getNamespace()]);
  isLoading.value = false;
};

onMounted(() => {
  initData();
  document.addEventListener('update-namespace-from-sidebar', getNamespace);
});

onUnmounted(() => {
  document.removeEventListener('update-namespace-from-sidebar', getNamespace);
});
</script>

<style lang="scss" scoped>
.namespace-main-settings-page {
  &__content {
    width: 39.813rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__title {
    display: block;
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.25rem;
    margin-top: 0;
    margin-bottom: 1rem;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__namespace-name {
    flex: 1;
  }
  &__button {
    margin-top: 3rem;
  }
}
</style>
